import { Resume } from "./resume";

export enum ResumeFilter {
  all = "All",
  archived = "Archived",
  expired = "Expired",
}

export type FilterableEvaluationSlug = NonNullable<
  Resume["local_evaluation_system"]
>;

export class Resumes extends Array<Resume> {
  constructor(resumes?: Resume[]) {
    super();
    if (resumes instanceof Array)
      resumes.forEach((resume) => this.push(resume));
  }

  public get(resumeId?: number): Resume | undefined {
    return this.find((resume) => resume.id == resumeId);
  }

  public update(newResume: Resume): Resumes {
    const existing = this.get(newResume.id);
    if (existing === undefined) return new Resumes([...this, newResume]);
    return new Resumes(
      this.map((resume) => {
        if (resume.id !== newResume.id) return resume;
        // has_similarity_signal is the only field carried over: it is list-only, so patchResume's response omits it and it would otherwise
        // vanish until the next list reload. Every other field comes from the PATCH response, which is authoritative and must not be overwritten.
        return new Resume({
          ...newResume,
          has_similarity_signal:
            newResume.has_similarity_signal ?? resume.has_similarity_signal,
        });
      }),
    );
  }

  public sortBy(
    propertyName: keyof Resume,
    direction: "asc" | "desc" = "asc",
  ): Resumes {
    const factor = direction === "asc" ? 1 : -1;

    return new Resumes(
      [...this].sort((a, b) => {
        const aValue = a[propertyName];
        const bValue = b[propertyName];

        if (aValue instanceof Date && bValue instanceof Date) {
          return (aValue.getTime() - bValue.getTime()) * factor;
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue) * factor;
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return (aValue - bValue) * factor;
        } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
          return (aValue === bValue ? 0 : aValue ? 1 : -1) * factor;
        } else if (aValue === null || aValue === undefined) {
          return factor;
        } else if (bValue === null || bValue === undefined) {
          return -factor;
        } else {
          return 0;
        }
      }),
    );
  }

  public filterBy(filter: ResumeFilter): Resumes {
    return new Resumes(
      this.filter((resume) => {
        if (filter === ResumeFilter.all)
          return resume.expired === false && resume.archived === false;
        if (filter === ResumeFilter.archived)
          return resume.expired === false && resume.archived === true;
        if (filter === ResumeFilter.expired) return resume.expired === true;
        return false;
      }),
    );
  }

  public filterByEvaluation(
    evaluation: FilterableEvaluationSlug | FilterableEvaluationSlug[],
  ): Resumes {
    const evaluations = Array.isArray(evaluation) ? evaluation : [evaluation];

    if (evaluations.length === 0) return this;

    return new Resumes(
      this.filter((resume) => {
        if (resume.local_evaluation_system === null) return false;
        return evaluations.includes(resume.local_evaluation_system);
      }),
    );
  }

  public filterByStage(stage: string | string[]): Resumes {
    const stages = Array.isArray(stage) ? stage : [stage];

    if (stages.length === 0) return this;

    return new Resumes(
      this.filter((resume) => {
        if (resume.stage === null) return false;
        return stages.includes(resume.stage);
      }),
    );
  }

  public queryBy(query: string): Resumes {
    return new Resumes(
      this.filter((resume) => {
        return resume.query(query);
      }),
    );
  }

  public shuffle(): Resumes {
    const array = [...this];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return new Resumes(array);
  }
}
